import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_risks: number
  critical_risks: number
  high_risks: number
  medium_risks: number
  low_risks: number
  risk_score_overall: number
}

interface Risk {
  id: string
  name: string
  category: string
  impact: string
  likelihood: string
  score: number
  owner: string
}

interface RiskCategory {
  category: string
  count: number
  avg_score: number
  trend: string
}

export default function RiskDashboardPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [risks, setRisks] = useState<Risk[]>([])
  const [categories, setCategories] = useState<RiskCategory[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, risksRes, catRes] = await Promise.all([
          fetch('/api/riskdashboard/statistics'),
          fetch('/api/riskdashboard/toprisks'),
          fetch('/api/riskdashboard/categories')
        ])
        setStatistics(await statsRes.json())
        setRisks(await risksRes.json())
        setCategories(await catRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getImpactBadge = (impact: string) => {
    const colors: Record<string, string> = {
      'Critical': 'bg-red-100 text-red-800',
      'High': 'bg-orange-100 text-orange-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    }
    return colors[impact] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Risk Dashboard...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Risk Dashboard</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_risks}</div>
              <div className="text-sm text-gray-500">Total Risks</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{statistics.critical_risks}</div>
              <div className="text-sm text-gray-500">Critical</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.high_risks}</div>
              <div className="text-sm text-gray-500">High</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-yellow-600">{statistics.medium_risks}</div>
              <div className="text-sm text-gray-500">Medium</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.low_risks}</div>
              <div className="text-sm text-gray-500">Low</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.risk_score_overall}</div>
              <div className="text-sm text-gray-500">Overall Score</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Top Enterprise Risks</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Risk</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Impact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Owner</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {risks.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{r.name}</td>
                      <td className="px-4 py-3 text-gray-600">{r.category}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getImpactBadge(r.impact)}`}>
                          {r.impact}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-red-600">{r.score}</td>
                      <td className="px-4 py-3 text-gray-600">{r.owner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Risk Categories</h2></div>
            <div className="p-4 space-y-3">
              {categories.map((c, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{c.category}</span>
                    <span className="text-sm text-gray-500">{c.count} risks</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span>Avg Score</span>
                      <span>{c.avg_score}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{width: `${c.avg_score}%`}}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
